import { observer } from "mobx-react-lite";
import { useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { ZIndex } from "@/domains/design/constants";
import { OAuthConsentPageContent } from "@/pages/oauth-consent/oauth-consent-page-content";
import { useAppStore } from "@/store";
import { oauthModule } from "@/modules/oauth";
import { api } from "@/modules/api";

const APP_NAME_LOAD_TIMEOUT_MS = 5_000;

export const OAuthConsentPage: FC = observer(() => {
  const [search] = useSearchParams();
  const { store } = useAppStore();

  const redirectUri = search.get("redirect_uri");
  const clientId = search.get("client_id");
  const state = search.get("state");
  const scopeStr = search.get("scope");
  const responseType = search.get("response_type");
  const codeChallenge = search.get("code_challenge");
  const codeChallengeMethod = search.get("code_challenge_method");

  const [appName, setAppName] = useState("Connected App");
  const [appScopes, setAppScopes] = useState<string[] | undefined>(undefined);
  const [isAppNameReady, setIsAppNameReady] = useState(false);

  const account = store.account.myAccount;

  const accountInfo = useMemo(
    () => ({
      profileDisplayName: account.profileDisplayName,
      profileEmailAddress: account.profileEmailAddress,
      profilePhotoUrl: account.profilePhotoUrl,
    }),
    [account.profileDisplayName, account.profileEmailAddress, account.profilePhotoUrl]
  );

  useEffect(() => {
    setAppName("Connected App");
    setAppScopes(undefined);
    setIsAppNameReady(false);

    let isCancelled = false;
    let isResolved = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const resolveAppMetadata = (name: string, scopes?: string[]) => {
      if (isCancelled || isResolved) return;
      isResolved = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      setAppName(name);
      setAppScopes(scopes);
      setIsAppNameReady(true);
    };

    const loadApplicationMetadata = async () => {
      if (!clientId) {
        resolveAppMetadata("Connected App");
        return;
      }

      try {
        const response = await api.get("/v2/oauth2/applications/{client_id}", {
          params: {
            path: {
              client_id: clientId,
            },
          },
        });
        const name = response.data?.name;
        const scopes = Array.isArray(response.data?.scopes)
          ? response.data.scopes
              .filter(
                (currentScope: unknown): currentScope is string => typeof currentScope === "string"
              )
              .map((currentScope: string) => currentScope.trim())
              .filter((currentScope: string) => currentScope.length > 0)
          : undefined;
        resolveAppMetadata(
          typeof name === "string" && name.trim() ? name.trim() : "Connected App",
          scopes
        );
      } catch (_error) {
        resolveAppMetadata("Connected App");
      }
    };

    timeoutId = setTimeout(() => {
      resolveAppMetadata("Connected App");
    }, APP_NAME_LOAD_TIMEOUT_MS);

    void loadApplicationMetadata();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [clientId]);

  const effectiveScopeStr = useMemo(() => {
    if (scopeStr && scopeStr.trim()) {
      return scopeStr.trim();
    }
    if (appScopes && appScopes.length > 0) {
      return appScopes.join(" ");
    }
    return undefined;
  }, [appScopes, scopeStr]);

  const handleApprove = useCallback(async (): Promise<string | undefined> => {
    if (!redirectUri || !clientId || !effectiveScopeStr) {
      return undefined;
    }

    const result = await oauthModule.approveOAuthConsent({
      clientId,
      redirectUri,
      scope: effectiveScopeStr,
      state: state ?? undefined,
      codeChallenge: codeChallenge ?? undefined,
      codeChallengeMethod: codeChallengeMethod ?? undefined,
    });

    const nextRedirectUrl =
      typeof result?.redirect_url === "string" && result.redirect_url.trim()
        ? result.redirect_url
        : undefined;

    return nextRedirectUrl;
  }, [clientId, codeChallenge, codeChallengeMethod, effectiveScopeStr, redirectUri, state]);

  const handleDeny = useCallback(async (): Promise<string | undefined> => {
    if (!redirectUri || !clientId || !effectiveScopeStr) {
      return undefined;
    }

    const result = await oauthModule.denyOAuthConsent({
      clientId,
      redirectUri,
      scope: effectiveScopeStr,
      state: state ?? undefined,
    });

    const nextRedirectUrl =
      typeof result?.redirect_url === "string" && result.redirect_url.trim()
        ? result.redirect_url
        : undefined;

    return nextRedirectUrl;
  }, [clientId, effectiveScopeStr, redirectUri, state]);

  if (!isAppNameReady) {
    return <PageWrapper />;
  }

  return (
    <PageWrapper>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.12 }}>
        <OAuthConsentPageContent
          redirectUri={redirectUri}
          clientId={clientId}
          state={state}
          scopeStr={effectiveScopeStr ?? null}
          responseType={responseType}
          appName={appName}
          accountInfo={accountInfo}
          onApprove={handleApprove}
          onDeny={handleDeny}
        />
      </motion.div>
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  -webkit-app-region: drag;
  z-index: ${ZIndex.ScreenOverlay};
  background: ${({ theme }) => theme.colors.surface.pane};
`;
